import type { TeamMemberInfo } from '@shared/api';
import { memo, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Spin } from 'antd';
import { Table } from '@shared/ui/table';
import styles from './team-list.module.scss';
import { DateService } from '@shared/services/date';
import { EmptyListState } from '@shared/ui/empty-list-state';
import { Settings } from './ui/settings';
import { buildAccessRights } from '@shared/constants';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import { sortTeamMembers } from './utils/sort-team-member';
import type { TeamSort } from '../../lib/types';

interface TeamListProps {
  teamMembers?: TeamMemberInfo[];
  isEmpty?: boolean;
  isLoading?: boolean;
  sort: TeamSort;
  onSort: (newSort: TeamSort) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

export const TeamList: FC<TeamListProps> = memo(
  ({ teamMembers, isEmpty, isLoading, sort, onSort, onDelete, onEdit }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'team' });
    const selfId = useAppSelector(authSelectors.selectUserInfo)?.id;
    const selfPermission = useAppSelector(authSelectors.selectSelfPermission);
    const accessRights = buildAccessRights(t);

    const sortedTeamMembers = useMemo(
      () => sortTeamMembers(teamMembers, sort),
      [teamMembers, sort],
    );

    const hasPermissionToSeeSettings = (targetMemberPermission: string) => {
      if (!selfPermission) return false;

      if (selfPermission === 'OWNER') return true;
      if (selfPermission === 'ADMIN') {
        return targetMemberPermission === 'VIEW' || targetMemberPermission === 'EDIT';
      }
      return false;
    };

    if (isLoading) {
      return (
        <Flex className={styles.loading}>
          <Spin />
        </Flex>
      );
    }

    if (isEmpty) {
      return (
        <Flex flex={1}>
          <EmptyListState title={t('emptyTitle')} />
        </Flex>
      );
    }

    return (
      <Table>
        <Table.THead isSticky>
          <Table.THeadCellText width={600}>{t('table.teammate')}</Table.THeadCellText>
          <Table.THeadCellSortable
            isSorted={sort === 'permission' || sort === '-permission' ? true : undefined}
            isDesc={
              sort === 'permission' || sort === '-permission' ? sort === '-permission' : undefined
            }
            onSort={() => onSort(sort === 'permission' ? '-permission' : 'permission')}
            width={120}
          >
            {t('table.access')}
          </Table.THeadCellSortable>
          <Table.THeadCellSortable
            isSorted={sort === 'created_at' || sort === '-created_at' ? true : undefined}
            isDesc={
              sort === 'created_at' || sort === '-created_at' ? sort === '-created_at' : undefined
            }
            onSort={() => onSort(sort === 'created_at' ? '-created_at' : 'created_at')}
            width={150}
          >
            {t('table.creationDate')}
          </Table.THeadCellSortable>
          <Table.THeadCell width={40}>
            <></>
          </Table.THeadCell>
        </Table.THead>
        <Table.TBody>
          {sortedTeamMembers?.map((teamMember) => (
            <Table.TRow
              key={teamMember.user_info.id}
              hasHover
              onClick={() => onEdit(teamMember.user_info.id)}
            >
              <Table.TCellUser
                user={{
                  fullName: teamMember.user_info?.full_name || '-',
                  caption: teamMember.user_info?.email || '-',
                }}
                permission={teamMember.permission}
              />

              <Table.TCellText variant="sm">
                {accessRights[teamMember?.permission]?.title || '-'}
              </Table.TCellText>

              <Table.TCellText variant="sm">
                {DateService.format(teamMember.joined_at, 'DD.MM.YYYY HH:mm')}
              </Table.TCellText>

              <Table.TCell>
                {selfId !== teamMember.user_info.id &&
                  hasPermissionToSeeSettings(teamMember.permission) && (
                    <Settings
                      onUpdate={() => onEdit(teamMember.user_info.id)}
                      onDelete={() => onDelete(teamMember.user_info.id)}
                    />
                  )}
              </Table.TCell>
            </Table.TRow>
          ))}
        </Table.TBody>
      </Table>
    );
  },
);
